import { TriangleAlertIcon } from "lucide-react";
import { Button } from "@/components/elements/Button";
import LoadingSpinner from "@/components/elements/LoadingSpinner";
import Center from "@/components/elements/layout/Center";
import { isRecoveredChunkPreloadError } from "@/helpers/chunk-preload-error";
import { useMountEffect } from "@/hooks/useMountEffect";
import { useMixpanel } from "@/monitoring/mixpanel";
import { datadogRum } from "@datadog/browser-rum";
import * as Sentry from "@sentry/react";

export const AppUnknownExceptionViewRenderer: Sentry.FallbackRender = (
  props,
) => {
  const message =
    props.error instanceof Error ? props.error.message : String(props.error);

  /**
   * A stale chunk that `chunk-preload-error.ts` is already reloading past shows
   * as a failed lazy import here, so a `React.lazy` route renders this boundary
   * for the moment before the reload lands. "An unknown error occurred" is both
   * wrong and about to disappear, and the Mixpanel and Datadog reports below
   * would describe a failure nobody experienced.
   *
   * Only true while a reload is actually in flight — if the handler could not
   * reload, the message is not marked recovered and the view below still shows.
   */
  if (isRecoveredChunkPreloadError(message)) {
    return (
      <Center className="h-screen w-screen">
        <LoadingSpinner />
      </Center>
    );
  }

  return <AppUnknownExceptionView {...props} />;
};

export const AppUnknownExceptionView = (
  props: Parameters<Sentry.FallbackRender>[0],
) => {
  const mixpanel = useMixpanel();

  const mpProps = { event_id: props.eventId };

  useMountEffect(() => {
    mixpanel.track("AppUnknownExceptionView Shown", mpProps);
    datadogRum.addError("AppUnknownExceptionView Shown", {
      ...mpProps,
      error:
        props.error instanceof Error
          ? props.error.message
          : String(props.error),
    });
  });

  return (
    <div className="flex h-screen w-screen items-center justify-center">
      <div className="flex max-w-md items-center space-x-10">
        <TriangleAlertIcon className="h-16 w-16 text-gray-500" />
        <div className="flex flex-col space-y-4">
          <div>An unknown error occurred, please try reloading the app</div>
          <Button
            variant="outline"
            colorScheme="primary"
            size="sm"
            onClick={() => {
              mixpanel.track(
                "AppUnknownExceptionView Reload Clicked",
                mpProps,
                () => {
                  window.location.reload();
                },
              );
            }}
          >
            Reload app
          </Button>
        </div>
      </div>
    </div>
  );
};
